/**
 * Shared form components for the look-up step.
 *
 * These components are used by both CreateDataFrameFromLookUpView (the main-view
 * flow for creating a look-up step) and LookUpSourceRightSidebar (the
 * right-sidebar flow for adding a new source step). They are deliberately
 * state-management-agnostic -- callers pass callbacks rather than owning state
 * -- so they work with both global state and local useState.
 *
 * Exports:
 *  - LookUpTableSelector: table selection step using AnalysisViewSelector
 *  - LookUpConfigEditor: step config editor for look-up configuration
 *  - getLookUpFormCompleteness: validation helper for form completeness
 *  - buildLookUpAddStepParams: builds params for addOrReplaceAndRunAnalysisStep
 */
import {css} from 'aphrodite';
import React, {useMemo} from 'react';

import AnalysisViewSelector from 'analysis-tool/ui/components/AnalysisViewSelector';
import {assert} from 'Asserts';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import {AddOrReplaceAndRunStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {stepperStyles} from 'ui-library/stepper/Stepper';
import {JSONValue} from 'util/Types';

// ---------------------------------------------------------------------------
// Validation helper
// ---------------------------------------------------------------------------

/**
 * Returns whether the look-up form is complete enough to finalize, and any
 * validation errors.
 */
export function getLookUpFormCompleteness(
  stepConfig: Record<string, JSONValue> | null,
  stepInput: PipelineViewGraphQL | null
): {isComplete: boolean | null; errors: Array<string>} {
  const stepInputDataFrame =
    stepInput != null ? getOutputDataForView(stepInput, undefined, PipelineStepDataKind.DATASET) : null;

  if (!stepConfig || !stepInputDataFrame) {
    return {isComplete: false, errors: []};
  }

  return validatePipelineStepConfig(PipelineStepType.LOOK_UP, 0, stepConfig, {
    'Input dataset': [stepInputDataFrame],
  });
}

// ---------------------------------------------------------------------------
// Finalize params builder
// ---------------------------------------------------------------------------

/**
 * Builds the params for `AnalysisActions.addOrReplaceAndRunAnalysisStep` for a
 * LOOK_UP step. Asserts that the form state is valid.
 */
export function buildLookUpAddStepParams(
  stepInput: PipelineViewGraphQL,
  stepConfig: Record<string, JSONValue>
): AddOrReplaceAndRunStepParams {
  const stepInputDataFrame = getOutputDataForView(stepInput, undefined, PipelineStepDataKind.DATASET);
  const inputLineageNode = stepInput.lineage[stepInput.lineage.length - 1];
  const stepOutputReference = inputLineageNode?.stepOutputReference;
  assert(
    inputLineageNode != null && stepOutputReference != null && stepInputDataFrame != null,
    'Tried to finalize with incomplete form'
  );

  return {
    stepType: PipelineStepType.LOOK_UP,
    stepVersion: 0,
    stepConfig: stepConfig as PipelineStepConfig<PipelineStepType.LOOK_UP, 0>,
    stepInputParents: {
      'Input dataset': [
        {
          stepId: inputLineageNode.step.id,
          stepDataName: stepOutputReference.outputStepData.name,
        },
      ],
    },
    stepInput: {inputDataFrameId: stepInputDataFrame.id},
  };
}

// ---------------------------------------------------------------------------
// LookUpTableSelector
// ---------------------------------------------------------------------------

export function LookUpTableSelector({
  analysis,
  selectedView,
  onSelect,
}: {
  analysis: PipelineGraphQL;
  selectedView: PipelineViewGraphQL | null;
  onSelect: (view: PipelineViewGraphQL | null) => void;
}): JSX.Element {
  return (
    <AnalysisViewSelector
      analysis={analysis}
      topLevelOnly={false}
      selectedView={selectedView}
      onSelect={onSelect}
      dataKind={PipelineStepDataKind.DATASET}
    />
  );
}

// ---------------------------------------------------------------------------
// LookUpConfigEditor
// ---------------------------------------------------------------------------

export function LookUpConfigEditor({
  stepConfig,
  setStepConfig,
  stepInput,
  clientStateId,
}: {
  stepConfig: Record<string, JSONValue> | null;
  setStepConfig: (config: Record<string, JSONValue> | null) => void;
  stepInput: PipelineViewGraphQL;
  clientStateId: string;
}): JSX.Element {
  const stepInputDataFrame = getOutputDataForView(stepInput, undefined, PipelineStepDataKind.DATASET);

  const dataFramesInfos = useMemo(
    () => ({
      'Input dataset': stepInputDataFrame == null ? [] : [{dataFrameSpec: stepInputDataFrame}],
    }),
    [stepInputDataFrame]
  );

  return (
    <StepConfigEditorPane
      stepType={PipelineStepType.LOOK_UP}
      stepVersion={0}
      config={stepConfig}
      setConfig={setStepConfig}
      dataFramesInfos={dataFramesInfos}
      clientStateId={clientStateId}
    />
  );
}

// ---------------------------------------------------------------------------
// Stepper step builders
// ---------------------------------------------------------------------------

/**
 * Returns the stepper steps for the look-up form, formatted with default
 * stepper styling. The configure step is only shown when `stepInput` is set.
 */
export function getLookUpStepperSteps({
  analysis,
  stepInput,
  onSelectStepInput,
  stepConfig,
  setStepConfig,
  clientStateId,
}: {
  analysis: PipelineGraphQL;
  stepInput: PipelineViewGraphQL | null;
  onSelectStepInput: (view: PipelineViewGraphQL | null) => void;
  stepConfig: Record<string, JSONValue> | null;
  setStepConfig: (config: Record<string, JSONValue> | null) => void;
  clientStateId: string;
}): Array<
  | {
      title: JSX.Element;
      content: JSX.Element;
      key: string;
    }
  | false
  | null
> {
  return [
    {
      title: <div className={css(stepperStyles.defaultStepTitle)}>Select table to look up from</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <LookUpTableSelector analysis={analysis} selectedView={stepInput} onSelect={onSelectStepInput} />
        </div>
      ),
      key: 'select-tables',
    },
    stepInput && {
      title: <div className={css(stepperStyles.defaultStepTitle)}>Configure look up</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <LookUpConfigEditor
            stepConfig={stepConfig}
            setStepConfig={setStepConfig}
            stepInput={stepInput}
            clientStateId={clientStateId}
          />
        </div>
      ),
      key: 'look-up',
    },
  ];
}
